"use client";

import { clsx } from "clsx";
import { Icon, type IconProps } from "../Icon";
import { AnimatePresence, motion } from "framer-motion";
import { Loading } from "../Loading";

type Merge<T, U> = DistributiveOmit<T, keyof U> & U;
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

type IconButtonElementType = React.ElementType<any, "button" | "a">;

type IconButtonProps<T extends IconButtonElementType = "button"> = Merge<
  React.ComponentPropsWithoutRef<T> & { as?: never },
  {
    loading?: boolean;
    size?: "sm" | "md" | "lg";
    variant?: "primary" | "secondary" | "ghost" | "destructive";
  } & Omit<IconProps, "className"> &
    Pick<
      JSX.IntrinsicElements["button"],
      | "onClick"
      | "onMouseEnter"
      | "onMouseLeave"
      | "children"
      | "disabled"
      | "type"
      | "tabIndex"
    >
>;

const styles = {
  base: "antialiased cursor-pointer text-black inline-flex items-center justify-center outline-none focus-visible:ring-2 ring-offset-1 ring-theme-accent hover:bg-gray-300 active:scale-95 transition duration-150 ease-in-out",
  variant: {
    primary:
      "bg-theme-button-primary-background-default border border-theme-accent hover:bg-theme-button-primary-background-hover",
    secondary: "bg-white border border-light",
    ghost: "bg-transparent",
    destructive: "bg-white border border-default hover:bg-gray-300",
  },
  size: {
    sm: "h-9 w-9 rounded-md",
    md: "h-10 w-10 rounded-md",
    lg: "h-12 w-12 rounded-md",
  },
  icon: {
    sm: "text-lg text-black",
    md: "text-lg text-black",
    lg: "text-xl text-black",
    ghost: "text-black",
    primary: "text-white",
    secondary: "text-black",
    destructive: "text-red-600",
  },
  disabled: "opacity-50 pointer-events-none",
};

const MotionLoading = motion(Loading);
const MotionIcon = motion(Icon);

export const IconButton = <T extends IconButtonElementType = "button">({
  as = "button" as T,
  size = "md",
  variant = "secondary",
  icon,
  loading,
  disabled,
  className,
  ...props
}: IconButtonProps<T>) => {
  const Element: IconButtonElementType = as;

  return (
    <Element
      className={clsx([
        styles.base,
        styles.size[size],
        styles.variant[variant],
        disabled && styles.disabled,
        className,
      ])}
      disabled={loading || disabled}
      {...props}
    >
      <AnimatePresence mode="wait" initial={false}>
        {loading ? (
          <MotionLoading
            initial={{ opacity: 0, position: "absolute", scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            size={size === "lg" ? "md" : "sm"}
          />
        ) : (
          <MotionIcon
            initial={{ opacity: 0, position: "absolute", scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            className={clsx([styles.icon[size], styles.icon[variant]])}
            icon={icon}
          />
        )}
      </AnimatePresence>
    </Element>
  );
};

IconButton.displayName = "IconButton";
